import { ReactNode } from "react";

interface MortgageFormLayoutProps {
  title: string;
  description: string;
  leftColumn: ReactNode;
  rightColumn: ReactNode;
  contactSection: ReactNode;
  actionButtons: ReactNode;
  resultsSection?: ReactNode;
  showResults: boolean;
}

export function MortgageFormLayout({
  title,
  leftColumn,
  rightColumn,
  contactSection,
  actionButtons,
  resultsSection,
  showResults,
}: MortgageFormLayoutProps) {
  return (
    <div className="w-full px-4 lg:px-6 space-y-8 pb-32">
             {/* Professional Header Section */}
             <div className="bg-white dark:bg-navy rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 overflow-hidden">
               {/* Header Banner */}
               <div className="bg-gradient-to-r from-cyan to-cyan-dark dark:from-cyan-light dark:to-cyan px-6 py-8 rounded-t-lg">
                 <div className="max-w-4xl mx-auto text-center">
                   <h1 className="text-4xl lg:text-5xl font-header text-white mb-3">{title}</h1>
                   <div className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full">
                     <span className="text-white font-subheader text-sm">FOR BROKER USE ONLY</span>
                   </div>
                 </div>
               </div>

               {/* Introduction Content */}
               <div className="px-6 py-8">
                 <div className="max-w-4xl mx-auto">
                   <div className="prose prose-gray dark:prose-invert max-w-none">
                     <div className="space-y-4 text-navy dark:text-gray-300 leading-relaxed">
                       <p className="text-lg">
                         Welcome! This calculator will give you an idea of how much we may lend and is currently for Residential lending only.
                       </p>
                       <p>
                         It is intended to be a guide only, does not provide a commitment or guarantee that we will offer a mortgage to your client(s) and a full calculation will be carried out at DIP stage.
                       </p>
                      <p>
                        Please refer to the help text{' '}
                        <span className="relative group inline-flex ml-2 align-middle">
                          <svg
                            className="h-4 w-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 cursor-help transition-colors"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </span>{' '}
                        on some items which provide more information as you complete the calculator.
                      </p>
                     </div>
                   </div>
                 </div>
               </div>

               {/* Contact Information Section */}
               <div className="bg-gray-50 dark:bg-navy-dark px-6 py-6 border-t border-gray-200 dark:border-gray-600">
                 <div className="max-w-4xl mx-auto">
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                     {/* Support Contact */}
                     <div className="space-y-3">
                       <h3 className="font-subheader text-navy dark:text-gray-100 text-lg">Need Help?</h3>
                       <p className="text-gray-600 dark:text-gray-400 text-sm">
                         If you have any questions, you can find our BDM contact information {' '}
                         <a
                           href="https://mhbs.co.uk/intermediaries/contact-us/"
                           target="_blank"
                           rel="noopener noreferrer"
                           className="text-cyan hover:text-cyan-dark dark:text-cyan-light dark:hover:text-cyan underline font-subheader"
                         >
                           here
                         </a> or call our Broker Team on <span className="font-subheader text-navy dark:text-gray-100">01858 412345</span>.
                       </p>
                       <p className="text-gray-600 dark:text-gray-400 text-sm">
                         You can find out more information about submitting a case {' '}
                         <a
                           href="https://mhbs.co.uk/intermediaries/submit-a-case/"
                           target="_blank"
                           rel="noopener noreferrer"
                           className="text-cyan hover:text-cyan-dark dark:text-cyan-light dark:hover:text-cyan underline font-subheader"
                         >
                           here
                         </a>.
                       </p>
                     </div>

                     {/* Useful Links */}
                     <div className="space-y-3">
                       <h3 className="font-subheader text-navy dark:text-gray-100 text-lg">Useful Resources</h3>
                       <div className="space-y-2">
                         <p className="text-gray-600 dark:text-gray-400 text-sm">
                           Our {' '}
                           <a
                             href="https://mhbs.co.uk/document/mhbs-intermediary-product-guide/"
                             target="_blank"
                             rel="noopener noreferrer"
                             className="text-cyan hover:text-cyan-dark dark:text-cyan-light dark:hover:text-cyan underline font-subheader"
                           >
                             Product Guide
                           </a> for details of all of our current products and rates
                         </p>
                         <p className="text-gray-600 dark:text-gray-400 text-sm">
                           <a
                             href="https://mhbs.co.uk/intermediaries/criteria-hub/"
                             target="_blank"
                             rel="noopener noreferrer"
                             className="text-cyan hover:text-cyan-dark dark:text-cyan-light dark:hover:text-cyan underline font-subheader"
                           >
                             Criteria Hub
                           </a>
                         </p>
                       </div>
                     </div>
                   </div>
                 </div>
               </div>
             </div>

      {/* Main Form Layout - Two Column on Large Screens */}
      <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 w-full">
        {/* Left Column */}
        <div className="flex-1 space-y-6">
          {leftColumn}
        </div>

        {/* Right Column */}
        <div className="flex-1 space-y-6">
          {rightColumn}
        </div>
      </div>

      {/* Contact Details Section */}
      {contactSection}

      {/* Spacer when no results are shown to prevent footer overlap */}
      {!showResults && <div className="h-24"></div>}

      {/* Calculate Button Bar - Fixed Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 shadow-lg border-t border-gray-200 dark:border-gray-700 z-40">
        <div className="w-full px-4 lg:px-6 py-4">
          <div className="flex justify-center items-center gap-4">
            {actionButtons}
          </div>
        </div>
      </div>

      {/* Results Section */}
      {showResults && resultsSection && (
        <div className="mt-8 pb-24">
          {resultsSection}
        </div>
      )}
    </div>
  );
}
